import {
  collectPlaybookCriteria,
  isPlaybookSectionNode,
  type PlaybookNode,
} from "@repo/app-types/entities/playbook";
import memoize from "lodash-es/memoize.js";
import { useMemo } from "react";

interface CriterionCoverageData {
  readonly criterionAverageSeconds: Record<string, number>;
  readonly topPerformerCriterionAverageSeconds: Record<string, number>;
}

/**
 * Shared derivations for both the per-teammate and the playbook-wide
 * criterion-coverage-seconds endpoints — they return the same shape, so the
 * same per-node lookups, the same shared scale, and the same "any data?"
 * boolean apply on either page.
 *
 * Section-level values are computed as the sum of their criterion-level
 * children (skipping sections with no covered criteria so we don't render a
 * stale "0s"). The shared `maxAverageCoverageSeconds` is the max over every
 * *criterion*-level value (own + benchmark) — section sums may exceed it and
 * clamp to the right edge of the bar by design.
 */
export function usePlaybookCriterionCoverageHelpers(
  data: CriterionCoverageData | undefined,
) {
  const hasAverageCoverageSeconds =
    data !== undefined && Object.keys(data.criterionAverageSeconds).length > 0;

  const getNodeAverageCoverageSeconds = useMemo(() => {
    const criterionAverageSeconds = data?.criterionAverageSeconds;
    if (!criterionAverageSeconds) {
      return () => undefined;
    }
    return memoize((node: PlaybookNode) =>
      sumOverCriteria(node, criterionAverageSeconds),
    );
  }, [data]);

  const getNodeTopPerformerAverageCoverageSeconds = useMemo(() => {
    const topPerformerAverageSeconds =
      data?.topPerformerCriterionAverageSeconds;
    if (!topPerformerAverageSeconds) {
      return () => undefined;
    }
    return memoize((node: PlaybookNode) =>
      sumOverCriteria(node, topPerformerAverageSeconds),
    );
  }, [data]);

  const maxAverageCoverageSeconds = useMemo(() => {
    if (!data) return undefined;
    const values = [
      ...Object.values(data.criterionAverageSeconds),
      ...Object.values(data.topPerformerCriterionAverageSeconds),
    ];
    if (values.length === 0) return undefined;
    const max = Math.max(...values);
    return max > 0 ? max : undefined;
  }, [data]);

  return {
    hasAverageCoverageSeconds,
    getNodeAverageCoverageSeconds,
    getNodeTopPerformerAverageCoverageSeconds,
    maxAverageCoverageSeconds,
  };
}

function sumOverCriteria(
  node: PlaybookNode,
  perCriterion: Record<string, number>,
): number | undefined {
  if (!isPlaybookSectionNode(node)) {
    return perCriterion[node.id];
  }
  let total = 0;
  let found = false;
  for (const criterion of collectPlaybookCriteria(node)) {
    const value = perCriterion[criterion.id];
    if (value !== undefined) {
      total += value;
      found = true;
    }
  }
  return found ? total : undefined;
}
